import { get, put } from 'services/request';

type ICheckerResult = {
  id: number;
};

type IMethod = 'memory' | 'google' | 'delete' | 'copy' | 'ai';

type IModelOptions = {
  render: () => void;
  onDone: () => void | Promise<void>;
};

type ITranslationProjectAsyncTotals = {
  total: number;
  pending: number;
  doing: number;
  done: number;
  failed: number;
  percent: number;
};

type ITranslationProjectAsync = {
  processing: boolean;
  totals: ITranslationProjectAsyncTotals;
};

type ITranslationProjectView = {
  id: number;
  name: string;
  freelance: number;
  analysis: unknown;
  deletable: boolean;
  async: ITranslationProjectAsync;
};

export default class TranslationProjectPreTranslateModel {
  projectId: number;
  render: () => void;
  onDone: () => void | Promise<void>;
  running = false;
  completed = false;
  method: IMethod = 'copy';
  private pollTimer: number | null = null;
  // Live progress from backend
  processing = false;
  progress: ITranslationProjectAsyncTotals = { total: 0, pending: 0, doing: 0, done: 0, failed: 0, percent: 0 };

  constructor(options: IModelOptions & { projectId: number }) {
    this.projectId = options.projectId;
    this.render = options.render;
    this.onDone = options.onDone;
  }

  public async preTranslate(method: IMethod) {
    this.method = method;
    this.running = false;
    this.completed = false;
    this.render();
    await this.kickoff();
    this.start();
  }

  public toggle() {
    if (this.running) {
      this.stop();
    } else {
      this.start();
    }
  }

  private start() {
    this.running = true;
    this.render();
    this.pollProgress();
  }

  // Check current project status and start polling if already processing
  public async startIfProcessing() {
    try {
      const proj = await get<ITranslationProjectView>(`/translations/projects/${this.projectId}`);
      if (proj.async && proj.async.processing) {
        this.processing = true;
        this.progress = proj.async.totals;
        this.start();
      }
    } catch (e) {
      // ignore
    }
  }

  private async kickoff() {
    await get<ICheckerResult[]>(
      `/translations/projects/${this.projectId}/pre-translate/pre-translate?method=${this.method}`,
    );
  }

  private async pollProgress() {
    if (!this.running || this.completed) return;
    try {
      const proj = await get<ITranslationProjectView>(`/translations/projects/${this.projectId}`);
      if (proj.async) {
        this.processing = proj.async.processing;
        this.progress = proj.async.totals;
      }
      if (!proj.async || proj.async.processing === false) {
        this.completed = true;
        this.stop();
        await this.onDone();
        return;
      }
      // Optionally update local progress state if needed
      this.render();
    } catch (e) {
      // Ignore transient errors and keep polling
    }
    this.pollTimer = window.setTimeout(() => this.pollProgress(), 1500);
  }

  public async cancel() {
    try {
      await put(`/translations/projects/${this.projectId}/pre-translate/cancel`, {});
    } catch (e) {
      // ignore
    } finally {
      this.completed = true;
      this.stop();
      await this.onDone();
    }
  }

  public stop() {
    if (this.running) {
      this.running = false;
      if (this.pollTimer) {
        window.clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      this.render();
    }
  }
}
